import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { ordersAPI } from '../../lib/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Pagination } from '../../components/Pagination';
import { DataTable } from '../../components/DataTable';
import { Eye } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorHandler';

const AdminOrders = () => {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { data: ordersRes, isLoading: loading, refetch: loadOrders } = useApiQuery(
    ['allOrders'],
    () => ordersAPI.getAll()
  );

  const allOrders = ordersRes?.data || [];
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const orders = allOrders.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const updateStatus = async (orderId, status) => {
    try {
      await ordersAPI.updateStatus(orderId, status);
      toast.success('Order status updated');
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to update status');
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-orders-page">
        <h1 className="text-3xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>Orders</h1>

        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : (
            <>
              <DataTable 
                columns={[
                  {
                    accessorKey: 'order_number',
                    header: 'Order #',
                    cell: ({ row }) => <span className="font-medium text-[#8acb95]">{row.original.order_number}</span>,
                  },
                  {
                    accessorKey: 'customer_name',
                    header: 'Customer',
                    cell: ({ row }) => (
                      <div>
                        <p className="font-medium text-sm">{row.original.customer_name}</p>
                        <p className="text-xs text-slate-500">{row.original.customer_email}</p>
                      </div>
                    )
                  },
                  {
                    accessorKey: 'customer_phone',
                    header: 'Phone',
                  },
                  {
                    id: 'items',
                    header: 'Items',
                    cell: ({ row }) => `${row.original.items.length} items`,
                  },
                  {
                    accessorKey: 'total_amount',
                    header: 'Total',
                    cell: ({ row }) => <span className="font-semibold">KWD {row.original.total_amount.toFixed(3)}</span>,
                  },
                  {
                    accessorKey: 'status',
                    header: 'Status',
                    cell: ({ row }) => <Badge className={getStatusColor(row.original.status)}>{row.original.status}</Badge>,
                  },
                  {
                    accessorKey: 'created_at',
                    header: 'Date',
                    cell: ({ row }) => <span className="text-sm text-slate-500">{new Date(row.original.created_at).toLocaleDateString()}</span>,
                  },
                  {
                    id: 'actions',
                    header: 'Actions',
                    cell: ({ row }) => (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewOrderDetails(row.original)}
                          data-testid={`view-order-${row.original.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select value={row.original.status} onValueChange={(status) => updateStatus(row.original.id, status)}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="processing">Processing</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ),
                  },
                ]} 
                data={orders} 
                isLoading={loading} 
                emptyMessage="No orders yet" 
              />
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={allOrders.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
            </>
          )}
        </div>

        {/* Order Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Order Details - {selectedOrder?.order_number}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-6">
                {/* Customer Info */}
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Customer Information</h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium">{selectedOrder.customer_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone</p>
                      <p className="font-medium">{selectedOrder.customer_phone}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{selectedOrder.customer_email}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-sm text-slate-500">Delivery Address</p>
                      <p className="font-medium">{selectedOrder.customer_address}</p>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Order Items</h3>
                  <div className="mt-4">
                    <DataTable 
                      columns={[
                        {
                          accessorKey: 'product_name',
                          header: 'Product',
                          cell: ({ row }) => <span className="font-medium text-slate-800">{row.original.product_name}</span>
                        },
                        {
                          accessorKey: 'product_code',
                          header: 'Code',
                          cell: ({ row }) => <span className="text-slate-500">{row.original.product_code}</span>
                        },
                        {
                          accessorKey: 'price',
                          header: 'Price',
                          cell: ({ row }) => `KWD ${row.original.price.toFixed(3)}`
                        },
                        {
                          accessorKey: 'quantity',
                          header: 'Qty',
                        },
                        {
                          id: 'total',
                          header: 'Total',
                          cell: ({ row }) => <span className="font-semibold">KWD {(row.original.price * row.original.quantity).toFixed(3)}</span>
                        }
                      ]}
                      data={selectedOrder.items}
                      isLoading={false}
                    />
                    <div className="flex justify-end p-4 border-b border-x bg-slate-50 border-slate-200 rounded-b-xl -mt-2">
                       <div className="text-right">
                         <span className="text-sm font-semibold mr-4 text-slate-600">Total Amount:</span>
                         <span className="font-bold text-lg text-[#8acb95]">KWD {selectedOrder.total_amount.toFixed(3)}</span>
                       </div>
                    </div>
                  </div>
                </div>

                {/* Order Info */}
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Order Information</h3>
                  <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-500">Payment Method</p>
                      <p className="font-medium">{selectedOrder.payment_method}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      <Badge className={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Order Date</p>
                      <p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Last Updated</p>
                      <p className="font-medium">{new Date(selectedOrder.updated_at).toLocaleString()}</p>
                    </div>
                    {selectedOrder.notes && (
                      <div className="col-span-2">
                        <p className="text-sm text-slate-500">Notes</p>
                        <p className="font-medium">{selectedOrder.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
